/**
 * Pure mapping from every pre-Settings URL to its canonical
 * `/settings/<section>/<item>` path (nav spec §4.6).
 *
 * The old URLs no longer redirect (no reason to keep redirects for renamed
 * routes — Chuck, 2026-09-11); this mapping survives for the one consumer
 * left, the persisted-tab migration (`migrateTabs` v8), which rewrites a
 * stored tab once so a workspace saved before the move still opens.
 */

/** Legacy paths that map one-to-one onto a Settings item. */
const EXACT: Record<string, string> = {
  '/assistant': '/settings/assistant/preferences',
  '/knowledge/ambient': '/settings/assistant/ambient',
  // Reminder settings moved out of Settings, onto the Agenda's Settings tab.
  '/my-data/reminders': '/agenda?tab=settings',
  '/my-data/observers': '/settings/assistant/observers',
  '/my-data/memories': '/settings/assistant/memories',
  '/my-data/highlights': '/settings/assistant/recollect',
  '/cost': '/settings/you/usage',
  '/settings/capabilities': '/settings/assistant/capabilities',
  '/settings/prompts': '/settings/assistant/capabilities',
  '/admin': '/settings/system/llm-providers',
  '/system': '/settings/system/llm-providers',
};

/** The Profile page's `?tab=` ids → the items those tabs became. */
const PROFILE_TABS: Record<string, string> = {
  about: '/settings/you/profile',
  preferences: '/settings/assistant/preferences',
  appearance: '/settings/you/appearance',
  connected: '/settings/you/connections',
  offline: '/settings/you/offline',
};

/**
 * Canonical target for a legacy `/profile` URL. The `tab` param picks the item
 * and is consumed; any other query params ride along unchanged.
 */
function profileTarget(search: string): string {
  // A stored tab path can carry `#hash` after the query; keep it out of the tab lookup.
  const hashAt = search.indexOf('#');
  const hash = hashAt === -1 ? '' : search.slice(hashAt);
  const params = new URLSearchParams(hashAt === -1 ? search : search.slice(0, hashAt));
  const tab = params.get('tab');
  params.delete('tab');
  const rest = params.toString();
  return `${PROFILE_TABS[tab ?? ''] ?? '/settings/you/profile'}${rest ? `?${rest}` : ''}${hash}`;
}

/**
 * Appends a `?query#hash` suffix to a target, joining with `&` when the target
 * already carries a query (`/agenda?tab=settings`), so the result has one `?`.
 */
function withSuffix(target: string, suffix: string): string {
  if (!target.includes('?') || !suffix.startsWith('?')) return `${target}${suffix}`;
  return suffix.length > 1 ? `${target}&${suffix.slice(1)}` : target;
}

/**
 * Canonical target for a legacy pathname + search, or `undefined` when the
 * path is not one Settings absorbed. The query string is preserved verbatim
 * (except `/profile`'s `tab`, which becomes part of the path), and a workflow
 * editor's `:id` survives the move under `/settings/assistant/capabilities`.
 */
export function settingsTarget(pathname: string, search: string): string | undefined {
  const exact = EXACT[pathname];
  if (exact) return withSuffix(exact, search);
  if (pathname === '/profile') return profileTarget(search);
  const admin = /^\/admin\/([^/]+)$/.exec(pathname);
  if (admin) return `/settings/system/${admin[1]}${search}`;
  const wf = /^\/settings\/(?:capabilities|prompts)\/workflows\/(new|[^/]+\/edit)$/.exec(pathname);
  if (wf) return `/settings/assistant/capabilities/workflows/${wf[1]}${search}`;
  return undefined;
}
